import React from 'react';
import { DropdownOption, ProfitCenterSummary as profitCenterSummaryInterface } from './DailyReviewInterfaces';
import { Button, Form, ProgressBar, Table } from 'react-bootstrap';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { map } from 'highcharts';
import dayjs from 'dayjs';
import './DailyReview.css';

export interface ProfitCenterSummaryProps {
    Summary: profitCenterSummaryInterface;
    searchedProfitCenter: DropdownOption | undefined;
    searchedDate: Date;
    formatPercentage(arg: number): string;
    formatDollar(arg: number): string;
    searching: boolean;
    formatTimeToUsersLocal(date: string): string;
}

const ProfitCenterSummary: React.FunctionComponent<ProfitCenterSummaryProps> = (props: ProfitCenterSummaryProps) => {
    const {
        DLSCount,
        ClosedDLSCount,
        EmployeeCount,
        EmployeesWithHoursCount,
        ProductionLastCalculatedDate,
        ProductionPercentage,
        TotalRevenue,
        TotalWages,
        eightWeekTicketCounts,
        sevenDayTicketCounts,
        ticketCategoryCounts,
        ticketTimeliness
    } = props.Summary;

    const { searchedProfitCenter, searchedDate, formatPercentage, formatDollar, searching, formatTimeToUsersLocal } = props;

    // F U N C T I O N S
    // 
    const calculateDlsProgress = (closedDlsCount: number, dlsCount: number) => { 
        if (dlsCount == null || dlsCount == 0) return 0
        return (closedDlsCount / dlsCount) * 100;
    };

    // 
    const calculateHoursEnteredProgress = (employeesWithHoursCount: number, employeeCount: number) => {
        if (employeeCount == null || employeeCount == 0) return 0;
        return (employeesWithHoursCount / employeeCount) * 100;
    };

    //
    const calculateEightWeekAverage = () => {
        if (eightWeekTicketCounts == null || eightWeekTicketCounts.length == 0) return 0;

        let ticketSum = 0;
        eightWeekTicketCounts.map((week) => {
            if (week.TicketCount != null) ticketSum += week.TicketCount;
        });
        return Math.round(ticketSum / eightWeekTicketCounts.length).toLocaleString('en-US');
    };

    //
    const getLastWeekDailyAverage = () => {
        if (eightWeekTicketCounts == null || eightWeekTicketCounts.length == 0) return 0;

        return (eightWeekTicketCounts[eightWeekTicketCounts.length - 1].TicketCount || 0).toLocaleString('en-US');
    }

    //
    const getTicketCountGraphData = () => {
        if (eightWeekTicketCounts == null || eightWeekTicketCounts.length == 0) return;

        const dataValue : number[][] = [];
        const firstWeek: number = eightWeekTicketCounts[0].WeekNumber;
        eightWeekTicketCounts.map((count, index) => {
            const datapoint = [ firstWeek + index + 1, count.TicketCount ];
            dataValue.push(datapoint);
        });
        const averageValue = calculateEightWeekAverage().toString().replace(',', '');   // if number is greater than 999, remove commas to render average line correctly
        const graphDataOptions = {
            chart: {
                height: 150,
                width: 300
            },
            title: { text: '' },
            yAxis: [{
                title: { text: '' },
                plotLines: [{
                    color: 'black',
                    width: 2,
                    value: averageValue,
                    label: { text: 'Daily Average', align: 'right', x: -20 }
                }]
            }],
            xAxis: { labels: {
                formatter: function (this: { value: number }) {
                    const x = this.value > 52 ? this.value - 52 : this.value;
                    return 'Week ' + x;
                }
            } },
            tooltip: { 
                formatter: function (this: { x: number, y: number }) {
                    const x = this.x > 52 ? this.x - 52 : this.x;  
                    return 'Week: ' + x + '<br />' + 'Average Ticket Count: ' + this.y;
                }
            },
            legend: { enabled: false },            
            series: [{
                name: 'Ticket Count',
                data: dataValue
            }]
        };

        return graphDataOptions;
    };

    //
    const renderCalendar = () => {
        if (sevenDayTicketCounts == null || sevenDayTicketCounts.length == 0) return;       

        if (sevenDayTicketCounts[0].FullDate > sevenDayTicketCounts[sevenDayTicketCounts.length - 1].FullDate) sevenDayTicketCounts.reverse();

        const monthLabel : string = dayjs(sevenDayTicketCounts[0].FullDate).format('MMMM');

        const firstDay : number = Number(dayjs(sevenDayTicketCounts[0].FullDate).format('d'));
        const prependDayCount : number = firstDay - 1;

        const lastDay : number = Number(dayjs(sevenDayTicketCounts[sevenDayTicketCounts.length - 1].FullDate).format('d'));
        const appendDayCount : number = 5 - lastDay;          
        
        return (
            <>
                <div>
                    <div className='daily-review-employee-summary-overview-item-label'>Tickets Received</div>
                    <div className='daily-review-employee-summary-overview-item-value'>{monthLabel}</div>
                </div>
                <br />
                <div>
                    <table className='daily-review-calendar'>
                        <thead>
                            <tr>
                                <th>Mon</th>
                                <th>Tue</th>
                                <th>Wed</th>
                                <th>Thu</th>
                                <th>Fri</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                {Array.apply(null, Array(prependDayCount)).map((x, index) => (
                                    <td key={`daily-review-calendar-before-placeholder-${index}`}></td>
                                ))}
                                {Array.apply(null, Array(5 - prependDayCount)).map((x, i) => (
                                    <td key={`daily-review-calendar-first-row-td-${i}`}>
                                        <div key={`daily-review-calendar-first-row-item-wrapper-${i}`} className='daily-review-calendar-day'>
                                            <div key={`daily-review-calendar-first-row-item-date-${i}`} className='daily-review-calendar-grid-item-a'>{dayjs(sevenDayTicketCounts[i].FullDate).format('D')}</div>
                                            <div key={`daily-review-calendar-first-row-item-count-${i}`} className='daily-review-calendar-grid-item-b'>{(sevenDayTicketCounts[i].TicketCount || 0).toLocaleString('en-US')}</div>
                                        </div>
                                    </td>
                                ))}
                            </tr>
                            <tr>
                                {Array.apply(null, Array(5 - appendDayCount)).map((x, i) => (
                                    <td key={`daily-review-calendar-second-row-${i}`}>
                                        <div key={`daily-review-calendar-second-row-item-wrapper-${i}`} className='daily-review-calendar-day'>
                                            <div key={`daily-review-calendar-second-row-item-date-${i}`} className='daily-review-calendar-grid-item-a'>{dayjs(sevenDayTicketCounts[(5 - prependDayCount) + i].FullDate).format('D')}</div>
                                            <div key={`daily-review-calendar-second-row-item-count-${i}`} className='daily-review-calendar-grid-item-b'>{(sevenDayTicketCounts[(5 - prependDayCount) + i].TicketCount || 0).toLocaleString('en-US')}</div>
                                        </div>
                                    </td>
                                ))}
                                {Array.apply(null, Array(appendDayCount)).map((x, index) => (
                                    <td key={`daily-review-calendar-after-placeholder-${index}`}></td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </>               
        )       
    };

    return (
        <div className='daily-review-summary-container'>  
            {searching && (
                <>
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                    <div className='daily-review-summary-dummy-div daily-review-div-loading-animation' />
                </>
            )}
            {!searching && (
                <>
                    <div className='daily-review-summary-section daily-review-vertical-flex'>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>{searchedProfitCenter?.label || ''}</div>
                            <div className='daily-review-employee-summary-overview-item-value'>{dayjs(searchedDate).format('dddd, MMMM D, YYYY')}</div>
                        </div>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>DLS Closed ({ClosedDLSCount || '0'} of {DLSCount || '0'})</div>
                            <ProgressBar>
                                <ProgressBar animated striped variant="success" now={calculateDlsProgress(ClosedDLSCount, DLSCount)} />                            
                                <ProgressBar animated striped variant="danger" now={100 - calculateDlsProgress(ClosedDLSCount, DLSCount)} />
                            </ProgressBar>                                
                        </div>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>Employee Hours Entered ({EmployeesWithHoursCount || '0'} of {EmployeeCount || '0'})</div>
                            <ProgressBar>
                                <ProgressBar animated striped variant="success" now={calculateHoursEnteredProgress(EmployeesWithHoursCount, EmployeeCount)} />
                                <ProgressBar animated striped variant='danger' now={100 - calculateHoursEnteredProgress(EmployeesWithHoursCount, EmployeeCount)} />
                            </ProgressBar>
                        </div>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>Last Calculated: {formatTimeToUsersLocal(ProductionLastCalculatedDate)}</div>
                        </div>
                    </div>
                    <div className='daily-review-summary-section daily-review-vertical-flex'>                             
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>Production</div>
                            <div className='daily-review-employee-summary-overview-item-value'>{formatPercentage(ProductionPercentage || 0)}</div>
                        </div>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>Pay</div>
                            <div className='daily-review-employee-summary-overview-item-value'>{formatDollar(TotalWages || 0)}</div>
                        </div>
                        <div>
                            <div className='daily-review-employee-summary-overview-item-label'>Revenue</div>
                            <div className='daily-review-employee-summary-overview-item-value'>{formatDollar(TotalRevenue || 0)}</div>
                        </div>                            
                    </div>
                    <div className='daily-review-summary-section'>
                        <div className='daily-review-employee-summary-overview-item-label'>8 Week Ticket Trend</div>
                        <div id="graph-container" className='daily-review-graph-container'>
                            <HighchartsReact highcharts={Highcharts} options={getTicketCountGraphData()} containerProps={{ style: { height: "100%", width: "100%" } }} />
                        </div>
                        <div className='daily-review-summary-section-horizontal'>
                            <div>
                                <div className='daily-review-employee-summary-overview-item-label'>8 Week Daily Average</div>
                                <div className='daily-review-employee-summary-overview-item-value'>{calculateEightWeekAverage()}</div>
                            </div>
                            <div>
                                <div className='daily-review-employee-summary-overview-item-label'>Last Week Daily Average</div>
                                <div className='daily-review-employee-summary-overview-item-value'>{getLastWeekDailyAverage()}</div>
                            </div>
                        </div>
                    </div>
                    <div className='daily-review-summary-section'>                            
                        {renderCalendar()}                                        
                    </div>
                    <div className='daily-review-summary-section'>
                        <div className='daily-review-employee-summary-overview-item-label'>{dayjs(searchedDate).format('M/DD')} DLS Tickets</div>
                        <Table borderless striped size='sm'>                                
                            <tbody>
                                {ticketCategoryCounts.map((item, index) => (
                                    <tr key={`daily-review-dls-tickets-row-${index}`}>
                                        <td key={`daily-review-dls-tickets-category-${index}`}>{item.Category || ''}</td>
                                        <td key={`daily-review-dls-tickets-count-${index}`}>{item.TicketCount || '0'}</td>
                                    </tr>
                                ))}                                    
                            </tbody>
                        </Table>
                    </div>
                    <div className='daily-review-summary-section'>
                    <div className='daily-review-employee-summary-overview-item-label'>Timeliness Ticket Counts</div>
                        <Table borderless striped size='sm'>
                            <thead>
                                <tr>
                                    <th>Timeliness</th>
                                    <th>Complete</th>
                                    <th>Carry Over</th>
                                    <th>Open</th>
                                    <th>Other</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketTimeliness.map((item, index) => (
                                    <React.Fragment key={`daily-review-ticket-timeliness-fragment-${index}`}>
                                        {item.TimeType != null && (
                                            <tr key={`daily-review-ticket-timeliness-row-${index}`}>
                                                <td key={`daily-review-ticket-timeliness-time-type-${index}`}>{item.TimeType || ''}</td>
                                                <td key={`daily-review-ticket-timeliness-complete-${index}`}>{item.Complete || 0}</td>
                                                <td key={`daily-review-ticket-timeliness-carryover-${index}`}>{item.Carryover || 0}</td>
                                                <td key={`daily-review-ticket-timeliness-open-${index}`}>{item.Open || 0}</td>
                                                <td key={`daily-review-ticket-timeliness-other-${index}`}>{item.Other || 0}</td>
                                            </tr>                                    
                                        )}
                                    </React.Fragment>
                                ))}                                    
                            </tbody>
                        </Table>
                    </div>                                    
                </>
            )}
        </div>
    )
}

export default ProfitCenterSummary;